import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactPopup from '../components/ContactPopup';

const PrivacyPolicy = () => {
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);

  const handleContactClick = () => {
    setIsContactPopupOpen(true);
  };

  useEffect(() => {
    // Set meta title and description
    document.title = 'Privacy Policy | Trademark My Brand Name';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Privacy Policy for Trademark My Brand Name. Learn how we collect, use, and protect your personal information when you use our trademark registration services.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Privacy Policy for Trademark My Brand Name. Learn how we collect, use, and protect your personal information when you use our trademark registration services.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);

  return (
    <>
      <Navbar onContactClick={handleContactClick} />
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/">
              <Button variant="outline" className="mb-6">
                <ArrowLeft className="mr-2" size={18} />
                Back to Home
              </Button>
            </Link>

          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <h1 className="text-4xl font-bold text-blue-900 mb-4">Privacy Policy</h1>

            <div className="space-y-6 text-gray-700 mt-8">
              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">1. Introduction</h2>
                <p className="leading-relaxed">
                  Welcome to Trademark My Brand Name. We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our trademark registration services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">2. Information We Collect</h2>
                <p className="leading-relaxed mb-3">
                  We collect information that you provide directly to us, including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Name, email address, phone number, and mailing address</li>
                  <li>Business information including company name and industry</li>
                  <li>Trademark information including marks, logos, and descriptions</li>
                  <li>Payment information for processing service fees</li>
                  <li>Communication preferences and correspondence with us</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">3. How We Use Your Information</h2>
                <p className="leading-relaxed mb-3">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide trademark registration and related legal services</li>
                  <li>Process and file your trademark applications with the USPTO</li>
                  <li>Communicate with you about your case and provide updates</li>
                  <li>Process payments and maintain financial records</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Send you relevant information about trademark law and our services</li>
                  <li>Improve our services and website functionality</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">4. Information Sharing and Disclosure</h2>
                <p className="leading-relaxed mb-3">
                  We do not sell your personal information. We may share your information with:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>The United States Patent and Trademark Office (USPTO) as required for trademark applications</li>
                  <li>Service providers who assist us in operating our business</li>
                  <li>Legal and regulatory authorities when required by law</li>
                  <li>Professional advisors such as attorneys and accountants</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">5. Data Security</h2>
                <p className="leading-relaxed">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">6. Your Rights</h2>
                <p className="leading-relaxed mb-3">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Access and review the personal information we hold about you</li>
                  <li>Request correction of inaccurate or incomplete information</li>
                  <li>Request deletion of your personal information (subject to legal obligations)</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Lodge a complaint with a supervisory authority</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">7. Cookies and Tracking Technologies</h2>
                <p className="leading-relaxed">
                  We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookies through your browser settings, though disabling cookies may affect website functionality.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">8. Children's Privacy</h2>
                <p className="leading-relaxed">
                  Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">9. Changes to This Privacy Policy</h2>
                <p className="leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-blue-900 mb-3">10. Contact Us</h2>
                <p className="leading-relaxed mb-3">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="font-semibold">Trademark My Brand Name</p>
                  <p>15233 Ventura Blvd, Sherman Oaks, CA 91403</p>
                  <p>Phone: +1 (123) 123-4567</p>
                  <p>Email: info@trademarkmybrandname.com</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      </div>
      <Footer />
      <ContactPopup 
        open={isContactPopupOpen} 
        onOpenChange={setIsContactPopupOpen} 
      />
    </>
  );
};

export default PrivacyPolicy;
